import { type KeyboardEvent, type ReactNode, useEffect, useRef, useState } from "react";
import { Button, Chip, Modal } from "@legal-500/legal500-design-system";
import {
  aiParser,
  type AiSchema,
  type AiSchemaTestResult,
  type AiTestDocument,
} from "@/api/aiParser.service";
import { usePermissions } from "@/hooks/usePermissions";
import { SubmissionDocumentPicker } from "./SubmissionDocumentPicker";
import styles from "./AiSchemas.module.css";

const DOCX_MIMETYPE = "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

/**
 * Splits JSON into coloured tokens.
 *
 * Deliberately regex-sized rather than a dependency: the input is always the output of
 * `JSON.stringify`, so it is already well-formed and pretty-printed, and the only question is
 * which run of characters is a key, a string, a number or a literal. Everything else — braces,
 * commas, indentation — falls through as plain text, so nothing can be dropped by a token the
 * pattern does not know about.
 */
const JSON_TOKENS =
  /("(?:\\.|[^"\\])*")(\s*:)|("(?:\\.|[^"\\])*")|\b(true|false|null)\b|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)/g;

function highlightJson(json: string): ReactNode[] {
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  for (const match of json.matchAll(JSON_TOKENS)) {
    const index = match.index ?? 0;
    if (index > lastIndex) nodes.push(json.slice(lastIndex, index));

    const [, propertyName, colon, string, literal, numeric] = match;
    if (propertyName !== undefined) {
      nodes.push(
        <span key={key++} className={styles.jsonKey}>
          {propertyName}
        </span>,
      );
      nodes.push(colon);
    } else if (string !== undefined) {
      nodes.push(
        <span key={key++} className={styles.jsonString}>
          {string}
        </span>,
      );
    } else if (literal !== undefined) {
      nodes.push(
        <span key={key++} className={styles.jsonLiteral}>
          {literal}
        </span>,
      );
    } else if (numeric !== undefined) {
      nodes.push(
        <span key={key++} className={styles.jsonNumber}>
          {numeric}
        </span>,
      );
    }

    lastIndex = index + match[0].length;
  }

  if (lastIndex < json.length) nodes.push(json.slice(lastIndex));
  return nodes;
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  const seconds = ms / 1000;
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${Math.round(seconds - minutes * 60)}s`;
}

function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  return `${minutes}m ${seconds - minutes * 60}s`;
}

type TestSource = "upload" | "submission";

const TEST_SOURCES: { value: TestSource; label: string }[] = [
  { value: "upload", label: "Upload a document" },
  { value: "submission", label: "Select an existing submission" },
];

const SOURCE_PANEL_ID = "ai-schema-test-source-panel";

function sourceTabId(source: TestSource): string {
  return `ai-schema-test-source-${source}`;
}

function SourceTabs({
  active,
  disabled,
  onChange,
}: {
  active: TestSource;
  disabled: boolean;
  onChange: (source: TestSource) => void;
}) {
  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number) => {
    const size = TEST_SOURCES.length;
    let nextIndex: number | null = null;
    if (event.key === "ArrowRight") nextIndex = (index + 1) % size;
    else if (event.key === "ArrowLeft") nextIndex = (index - 1 + size) % size;
    else if (event.key === "Home") nextIndex = 0;
    else if (event.key === "End") nextIndex = size - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const next = TEST_SOURCES[nextIndex].value;
    onChange(next);
    document.getElementById(sourceTabId(next))?.focus();
  };

  return (
    <div className={styles.sourceTabs} role="tablist" aria-label="Document source">
      {TEST_SOURCES.map(({ value, label }, index) => {
        const isActive = value === active;
        return (
          <button
            key={value}
            type="button"
            role="tab"
            id={sourceTabId(value)}
            aria-selected={isActive}
            aria-controls={SOURCE_PANEL_ID}
            tabIndex={isActive ? 0 : -1}
            disabled={disabled}
            className={`${styles.sourceTab}${isActive ? ` ${styles.sourceTabActive}` : ""}`}
            onClick={() => onChange(value)}
            onKeyDown={event => handleKeyDown(event, index)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

type TestSchemaModalProps = {
  /** The schema to test. Null closes the dialog — and discards the result with it. */
  schema: AiSchema | null;
  onClose: () => void;
};

/**
 * Upload a .docx or pick an existing submission document, run one schema against it, read what
 * came out.
 *
 * This is the only place in staff-ui that shows extracted submission content, and it is the
 * deliberate exception to the rule that none of it does: the document on screen is one the person
 * at the keyboard has just chosen, to see what this schema makes of it.
 * Nothing is saved — the server writes no blob and no parse run — so the result lives exactly as
 * long as this dialog. Closing it is how you throw it away.
 */
export function TestSchemaModal({ schema, onClose }: TestSchemaModalProps) {
  const { hasPermission } = usePermissions();
  const canTestExistingSubmission = hasPermission("submission.parsing.download");
  const [source, setSource] = useState<TestSource>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [submissionDocument, setSubmissionDocument] = useState<AiTestDocument | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [running, setRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [result, setResult] = useState<AiSchemaTestResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [copyState, setCopyState] = useState<"idle" | "copied" | "failed">("idle");

  const fileInputRef = useRef<HTMLInputElement>(null);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const schemaId = schema?.id ?? null;

  // A different schema — or the same one opened again — is a fresh test, never the last one's
  // output sitting under a new title.
  useEffect(() => {
    setSource("upload");
    setFile(null);
    setSubmissionDocument(null);
    setFileError(null);
    setResult(null);
    setError(null);
    setElapsedSeconds(0);
    setCopyState("idle");
  }, [schemaId]);

  useEffect(() => () => clearTimeout(copyTimeoutRef.current ?? undefined), []);

  // A run takes minutes, and a spinner with no number on it is indistinguishable from a hung
  // page. The count is what says the request is still alive.
  useEffect(() => {
    if (!running) return;
    const startedAt = Date.now();
    setElapsedSeconds(0);
    const timer = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [running]);

  if (!schema) return null;

  function chooseFile(chosen: File | undefined) {
    if (!chosen) return;
    if (!chosen.name.toLowerCase().endsWith(".docx")) {
      setFile(null);
      setFileError(`${chosen.name} is not a .docx — only Word documents can be extracted from.`);
      return;
    }
    setFileError(null);
    setFile(chosen);
    setResult(null);
    setError(null);
  }

  function changeSource(next: TestSource) {
    if (next === source) return;
    setSource(next);
    setFileError(null);
    setResult(null);
    setError(null);
  }

  function chooseSubmissionDocument(chosen: AiTestDocument | null) {
    setSubmissionDocument(chosen);
    setResult(null);
    setError(null);
  }

  function requestTest(schemaId: string) {
    if (source === "upload") return file ? aiParser.testSchema(schemaId, file) : null;
    return submissionDocument
      ? aiParser.testSchemaWithSubmission(schemaId, submissionDocument.submissionEntryFileId)
      : null;
  }

  async function runTest() {
    if (!schema) return;
    const pending = requestTest(schema.id);
    if (!pending) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const response = await pending;
      setResult(response.test);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setRunning(false);
    }
  }

  const documentJson = result === null ? null : JSON.stringify(result.document, null, 2);

  async function copyJson() {
    if (documentJson === null) return;
    clearTimeout(copyTimeoutRef.current ?? undefined);
    try {
      await navigator.clipboard.writeText(documentJson);
      setCopyState("copied");
    } catch {
      setCopyState("failed");
    } finally {
      copyTimeoutRef.current = setTimeout(() => setCopyState("idle"), 2000);
    }
  }

  return (
    <Modal isOpen={true} onClose={onClose} title={`Test ${schema.name}`} size="large">
      <div className={styles.testBody}>
        <p className={styles.testIntro}>
          Runs <strong>{schema.model}</strong> on {schema.provider} against one document, exactly as
          a real submission would.{" "}
          {source === "upload"
            ? "Nothing is saved: the file is not stored, no parse run is recorded, and the output below disappears when this closes."
            : "Nothing is saved: the submission is left untouched, no parse run is recorded, and the output below disappears when this closes."}
        </p>

        {canTestExistingSubmission && (
          <SourceTabs active={source} disabled={running} onChange={changeSource} />
        )}

        <div
          id={SOURCE_PANEL_ID}
          role={canTestExistingSubmission ? "tabpanel" : undefined}
          aria-labelledby={canTestExistingSubmission ? sourceTabId(source) : undefined}
          className={styles.sourcePanel}
        >
          {source === "upload" ? (
            <div
              className={`${styles.drop} ${isDragging ? styles.dropActive : ""}`}
              onDragOver={event => {
                event.preventDefault();
                if (!running) setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={event => {
                event.preventDefault();
                setIsDragging(false);
                if (running) return;
                chooseFile(event.dataTransfer.files[0]);
              }}
            >
              <p className={styles.dropTitle}>
                {file ? file.name : "Drop a .docx here, or choose one"}
              </p>
              <p className={styles.dropHint}>
                A submission document, up to 10MB. It is sent to the model and thrown away.
              </p>
              <input
                ref={fileInputRef}
                type="file"
                aria-label="Submission .docx to extract from"
                accept={`.docx,${DOCX_MIMETYPE}`}
                className={styles.hiddenInput}
                disabled={running}
                onChange={event => {
                  const chosen = event.target.files?.[0];
                  // Cleared so choosing the SAME file again still fires a change event — which is
                  // exactly what re-running after an edit to the schema looks like.
                  event.target.value = "";
                  chooseFile(chosen);
                }}
              />
              <div className={styles.dropActions}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={running}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Choose file
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!file || running}
                  onClick={() => void runTest()}
                >
                  {running ? "Running…" : "Run test"}
                </Button>
              </div>
            </div>
          ) : (
            <>
              <SubmissionDocumentPicker
                selected={submissionDocument}
                onSelect={chooseSubmissionDocument}
                disabled={running}
              />
              <div className={styles.submissionActions}>
                <Button
                  size="small"
                  variant="contained"
                  disabled={!submissionDocument || running}
                  onClick={() => void runTest()}
                >
                  {running ? "Running…" : "Run test"}
                </Button>
              </div>
            </>
          )}
        </div>

        {fileError && (
          <div className={styles.errorBanner} role="alert">
            {fileError}
          </div>
        )}

        {running && (
          <div className={styles.runningRow} role="status" aria-live="polite">
            <span className={styles.spinner} aria-hidden="true" />
            <span>
              Extracting… {formatElapsed(elapsedSeconds)} elapsed. A long submission takes several
              minutes — leave this open.
            </span>
          </div>
        )}

        {error && !running && (
          <div className={styles.errorBanner} role="alert">
            {error}
          </div>
        )}

        {result && !running && (
          <>
            <div className={styles.testSummary}>
              <Chip
                size="small"
                variant="filled"
                className={result.valid ? styles.onChip : styles.offChip}
              >
                {result.valid ? "Valid against the schema" : "Invalid"}
              </Chip>
              {result.classification === "sample" && (
                <Chip size="small" variant="filled" className={styles.sampleChip}>
                  SAMPLE
                </Chip>
              )}
              <span className={styles.testMetric}>
                {result.fieldsPopulated} of {result.fieldsTotal} fields populated
              </span>
              <span className={styles.testMetric}>
                {result.promptTokens.toLocaleString()} prompt +{" "}
                {result.completionTokens.toLocaleString()} completion tokens
              </span>
              <span className={styles.testMetric}>{formatDuration(result.durationMs)}</span>
              {result.attempt > 1 && (
                <span className={styles.testMetric}>Took {result.attempt} attempts</span>
              )}
              {result.repaired && <span className={styles.testMetric}>Output was repaired</span>}
              {result.finishReason === "length" && (
                <span className={styles.testMetric}>
                  Truncated at the model's output ceiling — the extraction is incomplete
                </span>
              )}
            </div>

            {result.issues.length > 0 && (
              <div className={styles.issues}>
                <p className={styles.issuesTitle}>
                  {result.issues.length} validation{" "}
                  {result.issues.length === 1 ? "issue" : "issues"}
                </p>
                <ul className={styles.issuesList}>
                  {result.issues.map(issue => (
                    <li key={`${issue.path}-${issue.message}`}>
                      <code className={styles.issuePath}>{issue.path}</code>{" "}
                      <span>{issue.message}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.jsonActions}>
              <Button size="small" variant="outlined" onClick={() => void copyJson()}>
                {copyState === "copied"
                  ? "Copied!"
                  : copyState === "failed"
                    ? "Copy failed"
                    : "Copy JSON"}
              </Button>
            </div>
            <pre className={styles.jsonPre} aria-label="Extracted JSON" tabIndex={0}>
              {documentJson === null ? "" : highlightJson(documentJson)}
            </pre>
          </>
        )}
      </div>
    </Modal>
  );
}
